import React, { useCallback, useEffect, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SeverityBadge from "@/components/shared/SeverityBadge";
import EmptyState from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import api, { extractApiError } from "@/lib/api";
import { filterUnrespondedByOrg } from "@/lib/incidentScope";
import { Bell, Check, MapPin, Activity, Clock, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { formatDisplayId } from "@/lib/utils";
import { downloadReport } from "@/lib/downloadReport";
import { useIncidentSocket } from "@/hooks/useIncidentSocket";
import LocationLabel from "@/components/shared/LocationLabel";

const SERVICE_RESPONSE_STATUSES = ["DISPATCHED", "EN_ROUTE", "ON_SCENE", "RESOLVED", "UNABLE_TO_RESPOND"];

function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function formatIncidentType(value) {
  const raw = String(value || "Incident").trim();
  return raw
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeSeverity(value) {
  const s = String(value || "low").toLowerCase();
  return ["critical", "high", "medium", "low"].includes(s) ? s : "low";
}

function formatEventTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function mapIncident(item) {
  const locationLabel =
    item?.location?.address ||
    item?.location ||
    item?.address ||
    item?.place ||
    [item?.city, item?.state].filter(Boolean).join(", ") ||
    "Location unavailable";

  return {
    id: item?.id || item?.incident_id || "",
    status: String(item?.status || "OPEN").toUpperCase(),
    type: formatIncidentType(item?.type || item?.incident_type),
    severity: normalizeSeverity(item?.severity),
    location: locationLabel,
    locationRaw: item?.location ?? item?.address ?? item?.place ?? null,
    timestamp: item?.created_at || item?.timestamp || item?.updated_at,
    distance_km: item?.distance_km,
  };
}

export default function AmbulanceIncoming() {
  const { can } = useAuth();
  const canUpdateIncidents = can("INCIDENTS.UPDATE");
  const canDispatchIncidents = can("INCIDENTS.DISPATCH");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 20, pages: 1 });

  const [statusSubmittingId, setStatusSubmittingId] = useState(null);

  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [responseIncident, setResponseIncident] = useState(null);
  const [responseStatus, setResponseStatus] = useState("DISPATCHED");
  const [responseNote, setResponseNote] = useState("");
  const [responseSubmitting, setResponseSubmitting] = useState(false);

  const fetchIncomingAlerts = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await api.get("/incidents", {
        params: {
          status: "OPEN",
          page,
          page_size: pageSize,
        },
      });

      const rows = filterUnrespondedByOrg(normalizeCollection(data)).map(mapIncident).filter((row) => row.id);
      setList(rows);
      setMeta(
        data?.meta || {
          total: rows.length,
          page,
          page_size: pageSize,
          pages: 1,
        },
      );
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message);
      setList([]);
      setMeta({ total: 0, page, page_size: pageSize, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchIncomingAlerts();
  }, [fetchIncomingAlerts]);

  useIncidentSocket((incidentData) => {
    const row = mapIncident(incidentData);
    if (!row.id) return;

    setList((prev) => {
      const exists = prev.some((item) => item.id === row.id);
      if (row.status !== "OPEN") {
        if (!exists) return prev;
        setMeta((current) => ({
          ...current,
          total: Math.max(0, (current?.total || 0) - 1),
        }));
        return prev.filter((item) => item.id !== row.id);
      }
      if (exists) {
        return prev.map((item) => (item.id === row.id ? row : item));
      }
      setMeta((current) => ({
        ...current,
        total: (current?.total || 0) + 1,
      }));
      return [row, ...prev].slice(0, pageSize);
    });
  });

  const updateIncidentStatus = async (incidentId, nextStatus) => {
    if (!canUpdateIncidents) {
      toast.error("You do not have permission to update incident status.");
      return;
    }
    setStatusSubmittingId(incidentId);
    try {
      await api.post(`/incidents/${incidentId}/service-response`, { status: nextStatus });
      toast.success(`Incident ${incidentId} status updated to ${nextStatus}.`);
      await fetchIncomingAlerts();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to update incident status.");
    } finally {
      setStatusSubmittingId(null);
    }
  };

  const openServiceResponseDialog = (incident) => {
    if (!canDispatchIncidents) {
      toast.error("You do not have permission to log service responses.");
      return;
    }
    setResponseIncident(incident);
    setResponseStatus("DISPATCHED");
    setResponseNote("");
    setResponseDialogOpen(true);
  };

  const submitServiceResponse = async () => {
    if (!canDispatchIncidents) {
      toast.error("You do not have permission to log service responses.");
      return;
    }
    if (!responseIncident?.id) return;

    setResponseSubmitting(true);
    try {
      const payload = {
        status: responseStatus,
        ...(responseNote.trim() ? { note: responseNote.trim() } : {}),
      };
      await api.post(`/incidents/${responseIncident.id}/service-response`, payload);
      toast.success(`Service response logged: ${responseStatus}.`);
      setResponseDialogOpen(false);
      await fetchIncomingAlerts();
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to log service response.");
    } finally {
      setResponseSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ambulance-incoming">
      <PageHeader
        title="Incoming Alerts"
        subtitle="Patient transport requests awaiting acceptance."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="h-10" onClick={fetchIncomingAlerts} disabled={loading}>
              {loading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
              Refresh
            </Button>
          </div>
        }
        onDownload={() => {
          downloadReport({
            filename: "ambulance_incoming_alerts",
            title: "Ambulance - Incoming Alerts Report",
            headers: ["ID", "Type", "Severity", "Location", "Time"],
            rows: list.map((i) => [i.id, i.type, i.severity, i.location, formatEventTime(i.timestamp)]),
          });
          toast.success("Report downloaded");
        }}
      />

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-medium text-slate-500">
          {loading ? "Loading incoming alerts..." : `${meta.total} open incidents`}
        </span>
        <span className="text-xs text-slate-500">
          Page {meta.page || page} of {meta.pages || 1}
        </span>
      </div>

      {list.length === 0 ? (
        <EmptyState title="No incoming alerts" description="Stay ready — new dispatches will surface here." icon={Bell} />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {list.map((i) => {
            const distance = typeof i.distance_km === "number" ? i.distance_km.toFixed(1) : "--";
            return (
              <div key={i.id} className="rounded-xl border border-slate-100 bg-white p-5 shadow-[0_2px_10px_-3px_rgba(15,23,42,0.05)]" data-testid={`alert-${i.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-widest text-slate-400">{formatDisplayId(i.id)}</p>
                    <p className="font-display text-lg font-bold text-slate-900">{i.type}</p>
                  </div>
                  <SeverityBadge severity={i.severity} />
                </div>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-700"><MapPin size={14} className="text-slate-400" /><LocationLabel locationValue={i.locationRaw ?? i.location} /></div>
                  <div className="flex items-center gap-2 text-slate-700"><Activity size={14} className="text-slate-400" /><span className="font-semibold">{distance} km</span> away</div>
                  <div className="flex items-center gap-2 text-slate-500"><Clock size={14} /><span className="text-xs">{formatEventTime(i.timestamp)}</span></div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {canUpdateIncidents ? (
                    <Button
                      className="bg-[hsl(var(--role-primary))] text-white hover:bg-[hsl(var(--role-primary))]/90"
                      onClick={() => updateIncidentStatus(i.id, "ACKNOWLEDGED")}
                      disabled={statusSubmittingId === i.id || responseSubmitting}
                      data-testid={`accept-${i.id}`}
                    >
                      {statusSubmittingId === i.id ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Check size={14} className="mr-1.5" />} Accept
                    </Button>
                  ) : null}
                  {canUpdateIncidents ? (
                    <Button
                      variant="outline"
                      onClick={() => updateIncidentStatus(i.id, "RESPONDING")}
                      disabled={statusSubmittingId === i.id || responseSubmitting}
                      data-testid={`dispatch-${i.id}`}
                    >
                      <Send size={14} className="mr-1.5" /> Dispatch
                    </Button>
                  ) : null}
                  {canDispatchIncidents ? (
                    <Button
                      variant="outline"
                      onClick={() => openServiceResponseDialog(i)}
                      disabled={statusSubmittingId === i.id || responseSubmitting}
                      data-testid={`log-response-${i.id}`}
                    >
                      Log Response
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Select
          value={String(pageSize)}
          onValueChange={(value) => {
            setPageSize(Number(value));
            setPage(1);
          }}
        >
          <SelectTrigger className="h-10 w-[110px]">
            <SelectValue placeholder="Size" />
          </SelectTrigger>
          <SelectContent>
            {[10, 20, 50].map((size) => (
              <SelectItem key={size} value={String(size)}>{size}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          disabled={loading || page <= 1}
          onClick={() => setPage((previousPage) => Math.max(1, previousPage - 1))}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          disabled={loading || page >= (meta.pages || 1)}
          onClick={() => setPage((previousPage) => Math.min(meta.pages || 1, previousPage + 1))}
        >
          Next
        </Button>
      </div>

      <Dialog open={responseDialogOpen} onOpenChange={(open) => !responseSubmitting && setResponseDialogOpen(open)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">Log Service Response</DialogTitle>
            <DialogDescription>
              Incident: <span className="font-mono">{formatDisplayId(responseIncident?.id)}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={responseStatus} onValueChange={setResponseStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_RESPONSE_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Note (Optional)</Label>
              <Textarea
                value={responseNote}
                onChange={(e) => setResponseNote(e.target.value)}
                placeholder="e.g. Unit AMB-12 dispatched from Bandra station"
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResponseDialogOpen(false)} disabled={responseSubmitting}>Cancel</Button>
            {canDispatchIncidents ? (
              <Button onClick={submitServiceResponse} disabled={responseSubmitting}>
                {responseSubmitting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
                Log Update
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
